import React, {
  ComponentProps,
  ComponentType,
  lazy,
  LazyExoticComponent,
  ReactNode,
  Suspense,
} from "react";
import { reloadServerContext } from "@utils/context";
import { Route } from "react-router";
import goto from "./pageDefinitions/goto";
import { useOnce } from "./hooks/once";
import ScrollToTop from "./components/scroll-to-top/ScrollToTop";
import { matchPath } from "react-router-dom";

declare const PAGE_ROUTES: Record<
  /** routeToken */ string,
  /** modulePath */ string
>;

/**
 * Get the lazy loaded component for the intended route. If it fails to do so,
 * reset to the baseline and track error.
 */
export function getLazyComponent<T extends ComponentType<any>>(
  dynamicImport: () => Promise<LazyExoticComponent<T>>,
  fallback: ReactNode = <div />
): T {
  const LazyLoadedComponent = lazy(() =>
    dynamicImport().catch((err: Error) => {
      // If loading the baseline fails, then send the user to /error.
      goto.error(err, "lazy-load");
      return null;
    })
  );

  // Ensure that we reload the server context on navigation
  return ((props: ComponentProps<T>) => {
    useOnce(() => {
      reloadServerContext();
    });

    return (
      <Suspense fallback={fallback}>
        <ScrollToTop />

        <LazyLoadedComponent {...props} />
      </Suspense>
    );
  }) as T;
}

export function getRouterComponents({
  routes = PAGE_ROUTES,
  fallback,
}: {
  routes?: Record<string, string>;
  fallback?: ReactNode;
} = {}) {
  return Object.keys(routes).map((path) => {
    const modulePath = routes[path];
    return (
      <Route
        key={path}
        exact
        path={`/${path}`}
        component={getLazyComponent(() => {
          // eslint-disable-next-line import/dynamic-import-chunkname
          return import(`./pages/${modulePath}`);
        }, fallback)}
      />
    );
  });
}

export function preloadPagePath(
  pathname: string,
  routes = PAGE_ROUTES
): Promise<void> {
  const matchingRoute = Object.keys(routes).find((key) =>
    matchPath(`/${pathname}`, { path: `/${key}` })
  );
  const matchingPath = routes[matchingRoute];
  if (matchingPath) {
    // eslint-disable-next-line import/dynamic-import-chunkname
    return import(`./pages/${matchingPath}`);
  }
  return Promise.resolve();
}
